import React, {useState, useEffect} from 'react';
import Table from '../../Components/Table';
import {FacilityColumns} from '../../ColumnsConfig/FacilityColumns';
import './../../App.css';
import {Box, Button, IconButton, InputBase, Paper, Chip,Alert} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FacilityFormDialog from '../../Modals/FacilityFormDialog';
import ConfirmationModal from '../../Common/ConfirmationModal';
import loaderGif from '../../Assests/loader.gif';
import { NotificationContainer, NotificationManager } from 'react-notifications';
import 'react-notifications/lib/notifications.css';
import FacilityService from '../../Services/Facility';

const Facility = () => {
  const [rows, setRows] = useState ([]);
  const [filteredRows, setFilteredRows] = useState ([]);
  const [searchValue, setSearchValue] = useState ('');
  const [chipValue, setChipValue] = useState ('');
  const [isDialogOpen, setIsDialogOpen] = useState (false);
  const [initialData, setInitialData] = useState (null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState (false);
  const [rowToDelete, setRowToDelete] = useState (null);
  const [isLoading, setIsLoading] = useState (false);
  const [totalRecords, setTotalRecords] = useState (0);
  const [currentPage, setCurrentPage] = useState (1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState (1);
  const [sortBy, setSortColumn] = useState (''); // Track the sorted column
  const [order, setSortOrder] = useState ('asc'); // Track the sorting order (asc or desc)
  const [recordDeleted, setRecordDeleted] = useState(false); 
  const [deleteError, setDeleteError] = useState(false);
  const [createRecord, setCreateRecord] = useState(false); 
  const [createRecordError, setCreateRecordError] = useState(false); 
  const [updateRecord, setUpdateRecord] = useState(false); 
  const [updateRecordError, setUpdateRecordError] = useState(false);
  const [facilityName, setFacilityName] = useState('');


  const validSortFields = ['name', 'address', 'city', 'state', 'contact'];

  const fetchFacilityData = async (
    page = currentPage,
    size = pageSize,
    sortByField = sortBy,
    sortOrder = order,
    searchTerm = facilityName
  ) => {
    setIsLoading(true);
    try {
      const sanitizedOrder = sortOrder === 'asc' || sortOrder === 'desc' ? sortOrder : 'asc'; // Default to 'asc' if invalid
      const queryObj = {
        page,
        pageSize: size,
        sortBy: validSortFields.includes(sortByField) ? sortByField : 'name',
        order: sanitizedOrder,
        searchTerm: searchTerm.trim() || "",
      };
  
      const response = await FacilityService.fetchFacility(
        queryObj.page,
        queryObj.pageSize,
        queryObj.sortBy,
        queryObj.order,
        queryObj.searchTerm,
      );
  
      const { data, totalRecords, totalPages } = response;
     
      if (Array.isArray(data)) {
        setRows(data.map(row => ({ ...row, id: row.facility_id })));
        setTotalRecords(totalRecords);
        setTotalPages(totalPages);
      } else {
        throw new Error('Unexpected API response format.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.ErrorMessage || "Failed to fetch Facility data.";
      console.error('Failed to fetch Facility data:', errorMessage);
      NotificationManager.error(errorMessage, "Error", 5000);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchFacilityData();
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (recordDeleted || deleteError || createRecord || createRecordError || updateRecord ||updateRecordError ) {
      const timer = setTimeout(() => {
        setRecordDeleted(false);
        setDeleteError(false);
        setCreateRecord(false);
        setCreateRecordError(false);
        setUpdateRecord(false);
        setUpdateRecordError(false)
    
      }, 5000); // Hide alert after 5 seconds
      return () => clearTimeout(timer); // Cleanup timer if component unmounts
    }
  }, [recordDeleted, deleteError,createRecord,createRecordError,updateRecord]);
  // // Handle sorting: Change sort field and order
  const handleSort = field => {
    const newSortOrder = sortBy === field && order === 'asc' ? 'desc' : 'asc';
    setSortColumn (field); // Update the sorted column
    setSortOrder (newSortOrder); // Toggle the sort order
    fetchFacilityData (currentPage, pageSize, searchValue, field, newSortOrder); // Fetch sorted data
  };
  const handleSearchChange = e => {
    setSearchValue (e.target.value);
    filterRows (e.target.value);
  };

  // Apply sorting to the entire data set, then paginate
  const filterRows = value => {
    let filteredData = rows.slice (); // Create a copy of the rows

    // Filter the sorted data based on the search value
    const searchFilteredData = filteredData.filter (
      row => row.name && row.name.toLowerCase ().includes (value.toLowerCase ())
    );

    // Paginate the filtered data
    setFilteredRows (
      searchFilteredData.slice (
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      )
    );
    setTotalRecords (searchFilteredData.length);
    setTotalPages (Math.ceil (searchFilteredData.length / pageSize));
  };

  
  const handleSearch = () => {
    const trimmedSearchTerm = facilityName.trim(); 
    setChipValue(trimmedSearchTerm);
    fetchFacilityData(1, pageSize, sortBy, order, trimmedSearchTerm); 
  };

  const handleChipDelete = async() => {
    setChipValue('');
    try{
      await fetchFacilityData(1, pageSize, sortBy, order, '');
  
    }catch(error){
      console.error('Error fetching SCAC data:', error);
      //NotificationManager.error("Failed to fetch SCAC data.", "Error", 5000);
    }finally{
      setFacilityName('');
    }
  };

  const handleDialogOpen = () => {
    setInitialData (null);
    setIsDialogOpen (true);
  };

  const handleDialogClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setIsDialogOpen (false);
    }
  };

  const handleSave = async (formData) => {
    try {
        const {name, address, city, state_id, zip,contact,phone_number } = formData;
        const dataToSubmit = { name, address, city, state_id, zip,contact,phone_number };

        if (formData.facility_id) {
            await FacilityService.updateFacility(formData.facility_id, dataToSubmit);
            NotificationManager.success("Facility Updated successfully.", "Success", 3000);

        } else {
            await FacilityService.createFacility(dataToSubmit);
            NotificationManager.success("Facility saved successfully.", "Success", 3000);
        }

        fetchFacilityData(); // Refresh the table data
        setIsDialogOpen(false);
    } catch (error) {
        const errorMessage = error.response?.data?.ErrorMessage || "Failed to save Facility.";
        console.error('Error saving Facility:', errorMessage);
        NotificationManager.error(errorMessage, "Error", 5000);
    }
};

 
  const handlePageChange = (newPage, newSize) => {
    setCurrentPage (newPage);
    setPageSize (newSize);
    fetchFacilityData (newPage, newSize, searchValue, sortBy, order); // Fetch new data for the specified page and size
  };

  const handleEdit = rowData => {
    setInitialData (rowData);
    setIsDialogOpen (true);
  };

  const inactivateFacility = async facilityId => {
    try {
      console.log (`Attempting to inactivate Facility with ID: ${facilityId}`);
      const response = await FacilityService.deleteFacility (facilityId);
      
      if (!response) {
        throw new Error('Failed to Inactive data');
      }
      const result = response.data
      console.log ('Inactivate result:', result);
      // return result;
      NotificationManager.success ('Facility deleted successfully.', 'Success', 3000);
      setIsDialogOpen (false); // Close the modal after successful save
    } catch (error) {
      console.error ('Error saving data:', error.message);
      if (error.message.includes ('400')) {
        NotificationManager.warning ('Please check your inputs.','Bad Request',5000);
      } else if (error.message.includes ('404')) {
        NotificationManager.info ('Resource not found.', 'Not Found', 5000);
      } else if (error.message.includes ('500')) {
        NotificationManager.error ('Server error. Please try again.', 'Internal Server Error',5000);
      } else {
        NotificationManager.error (`Error: ${error.message}`,'Save Failed',5000);
      }
    }
  };
  const handleDelete = rowData => {
    console.log ('Row data to delete:', rowData); // Debugging line
    setRowToDelete (rowData);
    setIsDeleteModalOpen (true);
  };

  const handleConfirmDelete = async () => {
    // if (!rowToDelete) return;
    setIsLoading (true);

    try {
      await inactivateFacility (rowToDelete.facility_id);
      console.log ('rowToDelete.facility_id', rowToDelete.facility_id);
      setRows (
        rows.filter (row => row.facility_id !== rowToDelete.facility_id)
      );
      setIsDeleteModalOpen (false);
      setRecordDeleted(true)
      NotificationManager.success("Facility Deleted successfully.", "Success", 3000);
      await fetchFacilityData ();
    } catch (error) {
      console.error('Error deleting facility type:', error);
      console.log("error update-------",error);
      console.error('Error updating data:', error.message);
      if (error.message.includes("400")) {
        NotificationManager.warning("Please check your inputs.", "Bad Request", 5000);
      } else if (error.message.includes("404")) {
        NotificationManager.info("Resource not found.", "Not Found", 5000);
      } else if (error.message.includes("500")) {
        NotificationManager.error("Server error. Please try again.", "Internal Server Error", 5000);
      } else {
        NotificationManager.error(`Error: ${error.message}`, "Save Failed", 5000);
      }
    }finally {
    setRowToDelete (null);
    setIsLoading(false); // Hide loading spinner when deleting data
  };
  };
  
  const handleCancelDelete = () => setIsDeleteModalOpen (false);

  return (
    <div className="px-5 my-1" style={{position: 'relative'}}>
       <NotificationContainer />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center">
          <Paper component="form" className="search-container" onSubmit={(e) => e.preventDefault()}>
            <InputBase
              className="search-input"
              placeholder="Search Facilities"
              inputProps={{'aria-label': 'search facilities'}}
              value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}
            />
            <IconButton
              type="button"
              aria-label="search"
              onClick={handleSearch}
            >
              <SearchIcon />
            </IconButton>
          </Paper>
          {chipValue &&
            <Chip
              label={chipValue}
              onDelete={handleChipDelete}
              deleteIcon={<CloseIcon />}
              className="chip-style"
            />}
        </Box>
        <Button
          variant="contained"
          onClick={handleDialogOpen}
          className="add-new-btn"
        >
          Add New Facility
        </Button>
      </Box>
      <Table
        //key={filteredRows.map(row => row.id).join(',')}
        rows={rows}
        columns={FacilityColumns (handleEdit, handleDelete)}
        className="table-header"
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        totalPages={totalPages}
        getRowId={row => row.facility_id}
        onSort={handleSort}
      />
      {isLoading &&
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255, 255, 255, 0.8)"
          zIndex={1}
        >
          <img src={loaderGif} alt="Loading..." width={50} height={50} />
        </Box>}

      <FacilityFormDialog
        open={isDialogOpen}
        handleClose={handleDialogClose}
        handleSave={handleSave}
        initialData={initialData}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        message="Are you sure you want to delete this Facility?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default Facility;
